import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadFilesToPublic, getFileUrl, UploadedFile } from '@/utils/fileUpload';
// Inline SVG Icons
const LogoutIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 17v-3h-5v-2h5v-3l4 3.5-4 3.5zm-2-11h-9v14h9v-2h-7v-10h7v-2z"/>
  </svg>
);

const ImageIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor">
    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
  </svg>
);

const NewspaperIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
  </svg>
);

const UploadIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
  </svg>
);
import './AdminPage.css';

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState<'gallery' | 'articles'>('gallery');

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <h1>KWCD Admin Dashboard</h1>
      </header>
      
      <nav className="admin-nav">
        <button
          className={`nav-button ${activeTab === 'gallery' ? 'active' : ''}`}
          onClick={() => setActiveTab('gallery')}
        >
          <ImageIcon /> Gallery
        </button>
        <button
          className={`nav-button ${activeTab === 'articles' ? 'active' : ''}`}
          onClick={() => setActiveTab('articles')}
        >
          <NewspaperIcon /> Articles
        </button>
      </nav>

      <main className="admin-content">
        {activeTab === 'gallery' ? (
          <GalleryManager />
        ) : (
          <ArticleManager />
        )}
      </main>
    </div>
  );
};

// Gallery Management Component
const GalleryManager = () => {
  const [galleries, setGalleries] = useState<Array<{id: number; title: string; images: string[]}>>([]);
  const [newGalleryTitle, setNewGalleryTitle] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Load existing galleries
  useEffect(() => {
    const fetchGalleries = async () => {
      console.log('Fetching data from server...');
      try {
        const response = await fetch('http://localhost:8000/api/galleries.php');
        if (response.ok) {
          const result = await response.json();
          console.log('Galleries loaded:', result);
          // Extract the data array from the response
          setGalleries(result.data || []);
        } else {
          throw new Error('Failed to fetch galleries');
        }
      } catch (error) {
        console.error('Error loading galleries:', error);
        console.log('Falling back to localStorage...');
        // Fallback to localStorage if API fails
        const savedGalleries = localStorage.getItem('galleries');
        if (savedGalleries) {
          console.log('Using galleries from localStorage');
          setGalleries(JSON.parse(savedGalleries));
        }
      }
    };

    fetchGalleries();
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files).slice(0, 20); // Limit to 20 files
      setSelectedFiles(files);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!newGalleryTitle.trim()) {
      alert('Please enter a title for the gallery');
      return;
    }
    
    if (selectedFiles.length === 0) {
      alert('Please select at least one image to upload');
      return;
    }
    
    console.log('Starting gallery upload...', { 
      title: newGalleryTitle, 
      fileCount: selectedFiles.length 
    });
    
    setIsUploading(true);
    
    try {
      console.log('Uploading files to server...');
      const uploadedFiles = await uploadFilesToPublic(selectedFiles);
      console.log('Files uploaded successfully:', uploadedFiles);
      
      const newGallery = {
        title: newGalleryTitle,
        images: uploadedFiles.map(file => file.filePath),
        createdAt: new Date().toISOString()
      };
      
      console.log('Saving gallery to database...', newGallery);
      
      const response = await fetch('http://localhost:8000/api/galleries.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newGallery)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save gallery');
      }
      
      const result = await response.json();
      if (result.success && result.data) {
        setGalleries(prev => [...prev, result.data]);
      } else {
        throw new Error('Invalid response format from server');
      }
      
      setNewGalleryTitle('');
      setSelectedFiles([]);
      alert('Gallery created successfully!');
    } catch (error) {
      console.error('Error in gallery upload:', error);
      alert('Failed to upload files. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const deleteGallery = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this gallery?')) return;
    
    try {
      const response = await fetch(`http://localhost:8000/api/galleries.php?id=${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete gallery');
      }
      
      const result = await response.json();
      if (result.success) {
        const updatedGalleries = galleries.filter(gallery => gallery.id !== id);
        setGalleries(updatedGalleries);
      } else {
        throw new Error(result.message || 'Failed to delete gallery');
      }
    } catch (error) {
      console.error('Error deleting gallery:', error);
      alert('Failed to delete gallery. Please try again.');
    }
  };

  return (
    <div className="gallery-manager">
      <h2>Manage Galleries</h2>
      
      <div className="upload-section">
        <h3>Create New Gallery</h3>
        <div className="form-group">
          <input
            type="text"
            placeholder="Enter gallery title"
            value={newGalleryTitle}
            onChange={(e) => setNewGalleryTitle(e.target.value)}
            className="gallery-title-input"
          />
        </div>
        <div className="form-group">
          <input
            type="file"
            id="gallery-upload"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            className="file-input"
          />
          <label htmlFor="gallery-upload" className="upload-label">
            <UploadIcon />
            {selectedFiles.length > 0 
              ? `${selectedFiles.length} file(s) selected (Max 20)` 
              : 'Choose Images (Max 20)'}
          </label>
          
          {selectedFiles.length > 0 && (
            <div className="selected-files">
              <h4>Selected Files ({selectedFiles.length}/20):</h4>
              <ul>
                {selectedFiles.map((file, index) => (
                  <li key={index} className="file-item">
                    <span>{file.name}</span>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(index);
                      }}
                      className="remove-file-button"
                      title="Remove file"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="upload-actions">
          <button 
            onClick={() => setSelectedFiles([])}
            disabled={selectedFiles.length === 0 || isUploading}
            className="secondary-button"
          >
            Clear Selection
          </button>
          <button 
            onClick={handleUpload} 
            disabled={!newGalleryTitle || selectedFiles.length === 0 || isUploading}
            className="primary-button"
          >
            {isUploading ? (
              <>
                <span className="spinner"></span>
                Uploading...
              </>
            ) : (
              `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`
            )}
          </button>
        </div>
      </div>
      
      <div className="existing-galleries">
        <h3>Existing Galleries</h3>
        {galleries.length === 0 ? (
          <p>No galleries found. Create your first gallery above.</p>
        ) : (
          <div className="gallery-grid">
            {galleries.map(gallery => (
              <div key={gallery.id} className="gallery-item">
                <div className="gallery-preview">
                  {gallery.images.length > 0 && (
                    <img src={gallery.images[0]} alt={gallery.title} />
                  )}
                  <span className="image-count">{gallery.images.length} images</span>
                </div>
                <h4>{gallery.title}</h4>
                <div className="gallery-actions">
                  <button className="edit-button">Edit</button>
                  <button 
                    className="delete-button"
                    onClick={() => deleteGallery(gallery.id)}
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Article Management Component
const ArticleManager = () => {
  interface Article {
    id: number;
    title: string;
    subtitle: string;
    date: string;
    content: string;
    image1: string;
    image2: string;
    createdAt?: string;  // Optional field for backward compatibility
  }

  const [articles, setArticles] = useState<Article[]>([]);
  
  const [newArticle, setNewArticle] = useState<{
    title: string;
    subtitle: string;
    date: string;
    content: string;
    image1: { path: string; name: string; loading: boolean };
    image2: { path: string; name: string; loading: boolean };
  }>(() => ({
    title: '',
    subtitle: '',
    date: new Date().toISOString().split('T')[0],
    content: '',
    image1: { path: '', name: '', loading: false },
    image2: { path: '', name: '', loading: false }
  }));
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load existing articles
  useEffect(() => {
    const fetchArticles = async () => {
      console.log('Fetching data from server...');
      try {
        const response = await fetch('http://localhost:8000/api/articles.php');
        if (response.ok) {
          const result = await response.json();
          console.log('API Response:', result);
          
          // Extract articles from the response - check both root level and data property
          let articlesData = [];
          if (Array.isArray(result)) {
            articlesData = result;
          } else if (result && Array.isArray(result.data)) {
            articlesData = result.data;
          } else if (result && result.success && Array.isArray(result.data)) {
            articlesData = result.data;
          }
          
          console.log('Extracted articles:', articlesData);
          
          if (articlesData.length > 0) {
            // Sort articles by date in descending order (newest first)
            const sortedArticles = [...articlesData].sort((a, b) => {
              try {
                // Try to get date in YYYY-MM-DD format from either date or createdAt
                const getDateValue = (article: Article) => {
                  // First try to get the date from the display format
                  const dateStr = article.date || article.createdAt || '';
                  // If it's already in YYYY-MM-DD format, use it directly
                  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                    return dateStr;
                  }
                  // Otherwise, try to parse it as a date and format it
                  const date = new Date(dateStr);
                  return isNaN(date.getTime()) ? '1970-01-01' : date.toISOString().split('T')[0];
                };
                
                const dateA = getDateValue(a);
                const dateB = getDateValue(b);
                
                // Compare dates in YYYY-MM-DD format (lexicographical comparison works)
                return dateB.localeCompare(dateA);
              } catch (error) {
                console.error('Error sorting articles:', error);
                return 0;
              }
            });
            
            console.log('Sorted articles:', sortedArticles);
            setArticles(sortedArticles);
          } else {
            console.log('No articles found in the response');
            setArticles([]);
          }
        } else {
          throw new Error('Failed to fetch articles');
        }
      } catch (error) {
        console.error('Error loading articles:', error);
        console.log('Falling back to localStorage...');
        // Fallback to localStorage if API fails
        const savedArticles = localStorage.getItem('articles');
        if (savedArticles) {
          console.log('Using articles from localStorage');
          setArticles(JSON.parse(savedArticles));
        }
      }
    };

    fetchArticles();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewArticle(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, imageNumber: 1 | 2) => {
    if (!e.target.files?.[0]) return;
    
    const file = e.target.files[0];
    const fileKey = `image${imageNumber}` as const;
    
    try {
      console.log(`Uploading image ${imageNumber}...`);
      // Show loading state
      setNewArticle(prev => ({ ...prev, [fileKey]: { ...prev[fileKey], loading: true } }));
      
      // Upload the file
      const [uploadedFile] = await uploadFilesToPublic([file]);
      const filePath = uploadedFile.filePath;
      
      console.log(`Image ${imageNumber} uploaded successfully:`, filePath);
      
      // Update the article with the new file path
      setNewArticle(prev => ({
        ...prev,
        [fileKey]: {
          ...prev[fileKey],
          path: filePath,
          name: file.name,
          loading: false
        }
      }));
    } catch (error) {
      console.error(`Error uploading image ${imageNumber}:`, error);
      alert('Failed to upload image. Please try again.');
      setNewArticle(prev => ({
        ...prev,
        [fileKey]: { ...prev[fileKey], loading: false }
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newArticle.title.trim() || !newArticle.content) {
      console.log('Article submission: Missing required fields');
      alert('Please fill in all required fields');
      return;
    }
    
    console.log('Submitting article...', { 
      title: newArticle.title,
      hasImage1: !!newArticle.image1.path,
      hasImage2: !!newArticle.image2.path
    });
    
    setIsSubmitting(true);
    
    try {
      // Upload images if they exist
      let image1Path = newArticle.image1.path || '';
      let image2Path = newArticle.image2.path || '';
      
      // No need to re-upload images as they were already uploaded in handleImageUpload
      console.log('Using pre-uploaded images:', { image1Path, image2Path });
      
      const articleData = {
        title: newArticle.title.trim(),
        subtitle: newArticle.subtitle.trim(),
        date: newArticle.date || new Date().toISOString().split('T')[0],
        content: newArticle.content.trim(),
        image1: image1Path,
        image2: image2Path
      };
      
      console.log('Saving article to database...', articleData);
      
      const response = await fetch('http://localhost:8000/api/articles.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(articleData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save article');
      }
      
      const result = await response.json();
      if (result.success && result.data) {
        setArticles(prev => [...prev, result.data]);
      } else {
        throw new Error('Invalid response format from server');
      }
      
      setNewArticle({
        title: '',
        subtitle: '',
        date: new Date().toISOString().split('T')[0],
        content: '',
        image1: { path: '', name: '', loading: false },
        image2: { path: '', name: '', loading: false }
      });
      
      alert('Article created successfully!');
    } catch (error) {
      console.error('Error in article submission:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      alert(`Failed to save article: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteArticle = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    
    try {
      const response = await fetch(`http://localhost:8000/api/articles.php?id=${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete article');
      }
      
      const result = await response.json();
      if (result.success) {
        const updatedArticles = articles.filter(article => article.id !== id);
        setArticles(updatedArticles);
      } else {
        throw new Error(result.message || 'Failed to delete article');
      }
    } catch (error) {
      console.error('Error deleting article:', error);
      alert('Failed to delete article. Please try again.');
    }
  };

  return (
    <div className="article-manager">
      <h2>Manage Articles</h2>
      
      <form onSubmit={handleSubmit} className="article-form">
        <h3>Create New Article</h3>
        
        <div className="form-group">
          <label>Title *</label>
          <input
            type="text"
            name="title"
            value={newArticle.title}
            onChange={handleInputChange}
            placeholder="Article Title"
            required
            className="form-control"
          />
        </div>
        
        <div className="form-group">
          <label>Subtitle</label>
          <input
            type="text"
            name="subtitle"
            value={newArticle.subtitle}
            onChange={handleInputChange}
            placeholder="Subtitle"
            className="form-control"
          />
        </div>
        
        <div className="form-group">
          <label>Date *</label>
          <input
            type="date"
            name="date"
            value={newArticle.date}
            onChange={handleInputChange}
            className="form-control"
            required
          />
        </div>
        
        <div className="form-group">
          <label>Content *</label>
          <textarea
            name="content"
            value={newArticle.content}
            onChange={handleInputChange}
            rows={6}
            required
          />
        </div>
        
        <div className="image-upload-group">
          <div className="form-group">
            <label>Main Image</label>
            <div className="file-upload">
              <label className="file-upload-label">
                <UploadIcon /> {newArticle.image1.path ? 'Change Image 1' : 'Choose Image 1'}
                {newArticle.image1.loading && <span className="loading-text">Uploading...</span>}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 1)}
                  style={{ display: 'none' }}
                />
              </label>
              {newArticle.image1.path && (
                <span className="file-name">
                  {newArticle.image1.name || 'Image 1'}
                </span>
              )}
            </div>
          </div>
          
          <div className="form-group">
            <label>Secondary Image (Optional)</label>
            <div className="file-upload">
              <label className="file-upload-label">
                <UploadIcon /> {newArticle.image2.path ? 'Change Image 2' : 'Choose Image 2'}
                {newArticle.image2.loading && <span className="loading-text">Uploading...</span>}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 2)}
                  style={{ display: 'none' }}
                />
              </label>
              {newArticle.image2.path && (
                <span className="file-name">
                  {newArticle.image2.name || 'Image 2'}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <button 
          type="submit" 
          disabled={isSubmitting || !newArticle.title || !newArticle.content}
          className="submit-button"
        >
          {isSubmitting ? 'Publishing...' : 'Publish Article'}
        </button>
      </form>
      
      <div className="existing-articles">
        <h3>Existing Articles</h3>
        {articles.length === 0 ? (
          <p>No articles found. Create your first article above.</p>
        ) : (
          <div className="articles-list">
            {articles.map(article => (
                <div key={article.id} className="article-item">
                  <div className="article-preview">
                    {article.image1 && <img src={article.image1} alt={article.title} />}
                    <div className="article-info">
                      <h4>{article.title}</h4>
                      <p className="article-date">
                        {(() => {
                          const dateStr = article.date || article.createdAt || '';
                          // If already in YYYY-MM-DD format, use it directly
                          if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                            return dateStr;
                          }
                          // Otherwise, try to parse and format the date
                          const date = new Date(dateStr);
                          return isNaN(date.getTime()) 
                            ? 'Invalid date' 
                            : date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
                        })()}
                      </p>
                    </div>
                  </div>
                  <div className="article-actions">
                    <button className="edit-button">Edit</button>
                    <button 
                      className="delete-button"
                      onClick={() => deleteArticle(article.id)}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
